import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, usePageMeta } from "@/components/site/Reveal";
import { DIVISIONS, MILESTONES, STATS, IMAGES } from "@/lib/content";

export default function Home() {
  usePageMeta(
    "SKN Group of Companies — Since 1928 | Excellence that defines modern India",
    "SKN Group of Companies — Excellence that defines modern India. A diversified conglomerate founded in 1928 in Kolkata: carpets, footwear, fragrances, real estate and investments. 9,000+ employees across 8 states."
  );

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <img
          src={IMAGES.hero}
          alt="Howrah Bridge, Kolkata at sunrise"
          className="absolute inset-0 w-full h-full object-cover img-grade"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F14] via-[#0B0F14]/55 to-[#0B0F14]/25" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-40 w-full text-[#F7F5F0]">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.35em] text-[#B8997A] mb-6">
              Since 1928 · Kolkata, India
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-medium tracking-tight leading-[1.02] max-w-4xl">
              SKN Group <span className="italic bronze">of</span> Companies
            </h1>
            <p className="mt-5 font-display italic text-lg md:text-2xl text-[#B8997A]" data-testid="hero-tagline">
              Excellence that defines modern India
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-base md:text-lg text-[#F7F5F0]/80 max-w-xl leading-relaxed">
              Nearly a century of enterprise. Born of the Swadeshi movement, grown
              across carpets, footwear, fragrances, real estate and investments —
              9,000+ people strong, across eight states of India.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/divisions"
                data-testid="hero-divisions-cta"
                className="bg-[#F7F5F0] text-[#0B0F14] px-8 py-4 text-sm font-medium inline-flex items-center gap-2 hover:bg-[#B8997A] transition-colors duration-300"
              >
                Explore Our Divisions <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <Link
                to="/journey"
                data-testid="hero-journey-cta"
                className="border border-[#F7F5F0]/40 text-[#F7F5F0] px-8 py-4 text-sm inline-flex items-center gap-2 hover:border-[#B8997A] hover:text-[#B8997A] transition-colors duration-300"
              >
                Our Journey
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Stats band */}
      <section className="ink-section border-t border-[#F7F5F0]/10" data-testid="stats-band">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-2 md:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1}>
              <p className="font-display text-4xl md:text-6xl font-medium tracking-tight">
                {s.value}
                <span className="bronze">{s.suffix}</span>
              </p>
              <p className="mt-3 text-xs uppercase tracking-[0.25em] text-[#F7F5F0]/50">
                {s.label}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* About teaser */}
      <section className="py-24 md:py-32" data-testid="about-teaser">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8997A] mb-6">Our Origin</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
                A modest venture near <span className="italic bronze">Howrah Bridge</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
                Our journey began in 1928, inspired by the spirit of the Swadeshi
                movement. We registered the SKNBM trademark in 1942, incorporated in
                1968, and have grown steadily across key sectors ever since — a journey
                defined by generations of perseverance.
              </p>
              <Link
                to="/about"
                data-testid="about-teaser-link"
                className="mt-8 inline-flex items-center gap-2 text-sm font-medium border-b border-foreground pb-1 hover:text-[#B8997A] hover:border-[#B8997A] transition-colors duration-300"
              >
                Read Our Story <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.2}>
              <figure className="overflow-hidden">
                <img
                  src={IMAGES.origin}
                  alt="Boats on the Hooghly river, Kolkata"
                  className="w-full h-[420px] object-cover img-grade hover:scale-105 transition-transform duration-700"
                />
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Divisions grid */}
      <section className="pb-24 md:pb-32" data-testid="divisions-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#B8997A] mb-6">What We Do</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight">
                  Six Divisions, <span className="italic bronze">One Legacy</span>
                </h2>
              </div>
              <Link
                to="/divisions"
                data-testid="divisions-grid-view-all"
                className="text-sm font-medium inline-flex items-center gap-2 border-b border-foreground pb-1 hover:text-[#B8997A] hover:border-[#B8997A] transition-colors duration-300 shrink-0"
              >
                View All Divisions <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-px bg-border">
            {DIVISIONS.map((d, i) => {
              const cardContent = (
                <>
                  <figure className="overflow-hidden h-56">
                    <img
                      src={d.image}
                      alt={d.name}
                      className="w-full h-full object-cover img-grade group-hover:scale-105 transition-transform duration-700"
                    />
                  </figure>
                  <div className="p-7">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{d.est}</p>
                    <h3 className="mt-2 font-display text-2xl font-medium tracking-tight group-hover:text-[#B8997A] transition-colors duration-300 flex items-center justify-between">
                      {d.name}
                      <ArrowUpRight size={18} strokeWidth={1.5} className="opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {d.companies.length > 0
                        ? d.companies.map((c) => c.name.split(" — ")[0]).join(" · ")
                        : "Investment & Trading Division"}
                    </p>
                  </div>
                </>
              );
              return (
                <Reveal key={d.id} delay={(i % 3) * 0.1} className="bg-background">
                  {d.external ? (
                    <a
                      href={d.external}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`division-card-${d.id}`}
                      className="group block"
                    >
                      {cardContent}
                    </a>
                  ) : (
                    <Link
                      to={`/divisions#${d.id}`}
                      data-testid={`division-card-${d.id}`}
                      className="group block"
                    >
                      {cardContent}
                    </Link>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Journey teaser */}
      <section className="ink-section py-24 md:py-32" data-testid="journey-teaser">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8997A] mb-6">The Journey</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight max-w-2xl leading-[1.05]">
              Nearly a century of <span className="italic bronze">entrepreneurial vision</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#F7F5F0]/10">
            {[MILESTONES[0], MILESTONES[3], MILESTONES[8]].map((m, i) => (
              <Reveal key={m.year} delay={i * 0.12} className="bg-[#0B0F14]">
                <div className="p-8 border-l border-[#B8997A]/30">
                  <p className="font-display text-5xl font-medium bronze">{m.year}</p>
                  <p className="mt-4 text-sm font-medium uppercase tracking-[0.15em]">{m.title}</p>
                  <p className="mt-3 text-sm text-[#F7F5F0]/60 leading-relaxed line-clamp-3">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/journey"
              data-testid="journey-teaser-cta"
              className="mt-14 inline-flex items-center gap-2 bg-[#F7F5F0] text-[#0B0F14] px-8 py-4 text-sm font-medium hover:bg-[#B8997A] transition-colors duration-300"
            >
              Our Journey So Far <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-24 md:py-32" data-testid="home-contact-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center md:text-left md:flex md:items-center md:justify-between gap-12">
          <Reveal>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
              Begin a <span className="italic bronze">conversation</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-lg">
              Partnerships, enquiries or opportunities across any of our divisions —
              our Kolkata head office is listening.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="shrink-0 mt-10 md:mt-0">
            <Link
              to="/contact"
              data-testid="home-contact-cta-button"
              className="inline-flex items-center gap-2 bg-foreground text-background px-10 py-5 text-sm font-medium hover:bg-[#B8997A] hover:text-[#0B0F14] transition-colors duration-300"
            >
              Contact Us <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
